import React from "react";
import PropTypes from "prop-types";
import styles from "./NameListTab.module.css";
import PinIcon from "../BadgeSection/Images/Pin_Icon.svg";

const ITEM_SHAPE = PropTypes.exact({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  avatarLabel: PropTypes.string.isRequired,
  avatarColor: PropTypes.string.isRequired,
  hasNote: PropTypes.bool.isRequired,
});

function splitAvatarLabel(label) {
  const match = String(label)
    .trim()
    .match(/^([A-Za-z]+)(\d+)$/);

  if (match) {
    return {
      top: match[1].toUpperCase(),
      bottom: match[2],
    };
  }

  return {
    top: String(label).trim().toUpperCase(),
    bottom: "",
  };
}

function NameListRow({ item, onItemClick, onPinClick }) {
  const avatarText = splitAvatarLabel(item.avatarLabel);

  return (
    <div
      className={styles.row}
      role="button"
      tabIndex={0}
      onClick={() => onItemClick(item.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onItemClick(item.id);
        }
      }}
      aria-label={`Open contact ${item.name}`}
    >
      <div className={styles.rowLeft}>
        <div
          className={styles.avatar}
          style={{ backgroundColor: item.avatarColor }}
          aria-hidden="true"
        >
          <span className={styles.avatarTop}>{avatarText.top}</span>
          {avatarText.bottom ? (
            <span className={styles.avatarBottom}>{avatarText.bottom}</span>
          ) : null}
        </div>
        <div className={styles.name}>{item.name}</div>
      </div>

      {item.hasNote ? (
        <button
          type="button"
          className={styles.pinButton}
          onClick={(event) => {
            event.stopPropagation();
            onPinClick(item.id);
          }}
          aria-label={`Open note for ${item.name}`}
        >
          <img src={PinIcon} alt="" className={styles.pinIcon} />
        </button>
      ) : null}
    </div>
  );
}

NameListRow.propTypes = {
  item: ITEM_SHAPE.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onPinClick: PropTypes.func.isRequired,
};

function NameListSection({
  title,
  items,
  onItemClick,
  onPinClick,
  showDivider,
}) {
  return (
    <section className={styles.section}>
      {showDivider ? <div className={styles.divider} /> : null}
      <h3 className={styles.sectionTitle}>{`${title} (${items.length})`}</h3>
      <div className={styles.sectionList}>
        {items.map((item) => (
          <NameListRow
            key={item.id}
            item={item}
            onItemClick={onItemClick}
            onPinClick={onPinClick}
          />
        ))}
      </div>
    </section>
  );
}

NameListSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(ITEM_SHAPE).isRequired,
  onItemClick: PropTypes.func.isRequired,
  onPinClick: PropTypes.func.isRequired,
  showDivider: PropTypes.bool,
};

NameListSection.defaultProps = {
  showDivider: false,
};

export function NameListTab({ prospects, customers, onPinClick, onItemClick }) {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <NameListSection
          title="Prospects"
          items={prospects}
          onItemClick={onItemClick}
          onPinClick={onPinClick}
        />
        <NameListSection
          title="Customers"
          items={customers}
          onItemClick={onItemClick}
          onPinClick={onPinClick}
          showDivider
        />
      </div>
    </div>
  );
}

NameListTab.propTypes = {
  prospects: PropTypes.arrayOf(ITEM_SHAPE).isRequired,
  customers: PropTypes.arrayOf(ITEM_SHAPE).isRequired,
  onPinClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
};
